// Paper-folding explosion: spawn N small triangles flying outward, fading over ~1.6s.

import * as THREE from 'three';

const FRAGMENT_COUNT = 14;

interface Fragment {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  spin: THREE.Vector3;
  ttl: number;
  ttlMax: number;
  baseOpacity: number;
}

export class ExplodeFx {
  private active: Fragment[] = [];
  scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  spawn(at: THREE.Vector3, color: number): void {
    for (let i = 0; i < FRAGMENT_COUNT; i++) {
      const size = 0.4 + Math.random() * 0.4;
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
         0, 0, 0,
         size, 0, 0,
         size * 0.5, size * (Math.random() * 0.6 + 0.4), 0,
      ]), 3));
      g.computeVertexNormals();
      const m = new THREE.MeshStandardMaterial({
        color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
        roughness: 0.7,
        metalness: 0.0,
        flatShading: true,
      });
      const mesh = new THREE.Mesh(g, m);
      mesh.position.copy(at);
      const dir = new THREE.Vector3(
        Math.random() * 2 - 1, Math.random() * 1.4 + 0.2, Math.random() * 2 - 1,
      ).normalize().multiplyScalar(6 + Math.random() * 6);
      const spin = new THREE.Vector3(
        Math.random() * 6 - 3, Math.random() * 6 - 3, Math.random() * 6 - 3,
      );
      this.scene.add(mesh);
      const ttlMax = 1.4 + Math.random() * 0.4;
      this.active.push({ mesh, velocity: dir, spin, ttl: ttlMax, ttlMax, baseOpacity: 1 });
    }
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const f = this.active[i];
      f.ttl -= dt;
      if (f.ttl <= 0) {
        this.scene.remove(f.mesh);
        f.mesh.geometry.dispose();
        (f.mesh.material as THREE.Material).dispose();
        this.active.splice(i, 1);
        continue;
      }
      // gravity + drag
      f.velocity.y -= 6 * dt;
      f.velocity.multiplyScalar(1 - 0.6 * dt);
      f.mesh.position.addScaledVector(f.velocity, dt);
      f.mesh.rotation.x += f.spin.x * dt;
      f.mesh.rotation.y += f.spin.y * dt;
      f.mesh.rotation.z += f.spin.z * dt;
      const t = f.ttl / f.ttlMax;
      const mat = f.mesh.material as THREE.MeshStandardMaterial;
      mat.opacity = Math.min(1, t * 1.4);
    }
  }
}
